import React from 'react';
import { motion } from 'framer-motion';
import { Cloud, CheckCircle, Zap, Shield, Settings, Play } from 'lucide-react';

const DEPLOYMENT_FEATURES = [
  {
    icon: Zap,
    title: "One-Click Deployment",
    description: "Deploy your entire AI Genes ecosystem in minutes, not months. No complex configurations, no technical headaches—just intelligent automation ready to transform your operations.",
    color: "from-blue-500 to-cyan-500"
  },
  {
    icon: Cloud,
    title: "Fully On-Premise Capable",
    description: "Deploy entirely within your own infrastructure—no data ever leaves your environment. Music to the ears of IT, security, and compliance teams who demand zero data leakage risk.",
    color: "from-emerald-500 to-teal-500"
  },
  {
    icon: CheckCircle,
    title: "End-to-End Testing",
    description: "Every deployment includes comprehensive testing of all AI Genes working together. Verify your workflows, validate integrations, and ensure everything works perfectly before going live.",
    color: "from-purple-500 to-pink-500"
  },
  {
    icon: Shield,
    title: "Enterprise-Grade Security",
    description: "Built-in security protocols, compliance frameworks, and audit trails. Your deployment meets the highest enterprise standards from day one.",
    color: "from-orange-500 to-red-500"
  }
];

const DeploymentSection: React.FC = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-400 via-emerald-400 to-purple-400 bg-clip-text text-transparent">
              Deploy in Minutes,
            </span>
            <br />
            <span className="text-white">Not Months</span>
          </h2>
          <p className="text-base text-gray-300 max-w-2xl mx-auto leading-relaxed">
            While your competitors struggle with complex implementations and data security concerns, you'll be transforming operations. 
            Deploy fully on-premise or in your preferred cloud—your data never leaves your control, your AI Genes work immediately.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {DEPLOYMENT_FEATURES.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-6 h-full hover:border-blue-500/30 transition-all duration-300">
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${feature.color} mb-4`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Deployment Process */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-3xl p-8 md:p-12"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Three Steps to Transformation
            </h3>
            <p className="text-base text-gray-300">
              From decision to deployment in record time
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Choose Your Environment",
                description: "Deploy on-premise for maximum security, or select your preferred cloud platform. Your data, your infrastructure, your control.",
                icon: Settings
              },
              {
                step: "02", 
                title: "One-Click Deploy",
                description: "Hit deploy and watch as all AI Genes are automatically configured and integrated.",
                icon: Play
              },
              {
                step: "03",
                title: "Test & Go Live",
                description: "Run comprehensive end-to-end tests, then flip the switch to transform your operations.",
                icon: CheckCircle
              }
            ].map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="relative mb-4">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full flex items-center justify-center mb-3">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                    {step.step}
                  </div>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">{step.title}</h4>
                <p className="text-sm text-gray-300">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mt-16"
        >
          <p className="text-base text-gray-300 mb-6">
            Ready to see how fast transformation can happen?
          </p>
          <button className="px-12 py-4 bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 text-white text-xl font-semibold rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(59,130,246,0.3)] hover:shadow-[0_0_40px_rgba(59,130,246,0.4)] hover:scale-105">
            Schedule Your Deployment Demo
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default DeploymentSection;
